import React from 'react';
import { Link } from 'react-router-dom';
import './ManagedPrograms.css';

const ManagedPrograms = () => {
  return (
    <div className="managed-page">
      {/* Hero Section */}
      <section className="managed-hero">
        <div className="managed-hero-container">
          <div className="managed-hero-content">
            <p className="managed-hero-breadcrumb">
              <Link to="/services/software">Software Services</Link> / Managed Programs
            </p>
            <h1 className="managed-hero-title">Managed Programs</h1>
            <p className="managed-hero-description">
              Marketing and social media management that drives measurable outcomes. We handle the strategy, execution, and optimization so you can focus on running your business — not managing campaigns.
            </p>
            <Link to="/contact" className="btn-managed-hero">Get Started</Link>
          </div>
          <div className="managed-hero-image">
            <img
              src="https://images.unsplash.com/photo-1533750349088-cd871a92f312?w=800&q=80"
              alt="Marketing and campaign management dashboard"
            />
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="managed-overview">
        <div className="managed-container">
          <h2 className="managed-section-title">Why Managed Programs?</h2>
          <p className="managed-overview-text">
            Most businesses know they need a strong digital presence, but few have the bandwidth to execute consistently. Our managed programs give you a dedicated team that treats your brand like their own — building campaigns, managing your social presence, and tracking every dollar back to results.
          </p>
          <div className="managed-stats-row">
            <div className="managed-stat">
              <span className="managed-stat-number">20+</span>
              <span className="managed-stat-label">Years of Experience</span>
            </div>
            <div className="managed-stat">
              <span className="managed-stat-number">360°</span>
              <span className="managed-stat-label">Full-Funnel Coverage</span>
            </div>
            <div className="managed-stat">
              <span className="managed-stat-number">1</span>
              <span className="managed-stat-label">Accountable Partner</span>
            </div>
          </div>
        </div>
      </section>

      {/* Service 1: Marketing Automation */}
      <section className="managed-service managed-service-light">
        <div className="managed-container">
          <div className="managed-service-grid">
            <div className="managed-service-content">
              <span className="managed-service-tag">01</span>
              <h2 className="managed-service-title">Marketing Automation</h2>
              <p className="managed-service-description">
                Turn manual, repetitive marketing tasks into automated workflows that nurture leads, personalize outreach, and convert prospects — all without adding headcount. We design, build, and manage automation systems tailored to how your business actually operates.
              </p>
              <ul className="managed-service-features">
                <li>
                  <strong>Lead Nurturing Workflows</strong>
                  <span>Automated email sequences that move prospects through your funnel based on behavior and engagement signals.</span>
                </li>
                <li>
                  <strong>CRM-Integrated Campaigns</strong>
                  <span>Campaigns wired directly into your CRM so sales and marketing stay aligned with real-time data.</span>
                </li>
                <li>
                  <strong>Audience Segmentation</strong>
                  <span>Dynamic segmentation based on demographics, behavior, and lifecycle stage for targeted messaging.</span>
                </li>
                <li>
                  <strong>A/B Testing & Optimization</strong>
                  <span>Continuous testing of subject lines, content, and timing to improve open rates and conversions.</span>
                </li>
              </ul>
            </div>
            <div className="managed-service-visual">
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80"
                alt="Marketing automation dashboard and analytics"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Service 2: Social Media Management */}
      <section className="managed-service managed-service-white">
        <div className="managed-container">
          <div className="managed-service-grid managed-service-grid-reverse">
            <div className="managed-service-content">
              <span className="managed-service-tag">02</span>
              <h2 className="managed-service-title">Social Media Management</h2>
              <p className="managed-service-description">
                A consistent, professional social presence takes more than occasional posts. We handle your social strategy end-to-end — from content planning and creation to community engagement and performance reporting.
              </p>
              <ul className="managed-service-features">
                <li>
                  <strong>Content Strategy & Calendars</strong>
                  <span>Monthly editorial calendars aligned to your brand voice, industry trends, and business goals.</span>
                </li>
                <li>
                  <strong>Platform Management</strong>
                  <span>Active management across LinkedIn, Facebook, Instagram, X, and other platforms relevant to your audience.</span>
                </li>
                <li>
                  <strong>Community Engagement</strong>
                  <span>Timely responses to comments, messages, and mentions to build trust and grow your audience.</span>
                </li>
                <li>
                  <strong>Performance Reporting</strong>
                  <span>Monthly analytics with actionable insights on reach, engagement, follower growth, and content performance.</span>
                </li>
              </ul>
            </div>
            <div className="managed-service-visual">
              <img
                src="https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=600&q=80"
                alt="Social media content and engagement"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Service 3: Campaign Tracking */}
      <section className="managed-service managed-service-light">
        <div className="managed-container">
          <div className="managed-service-grid">
            <div className="managed-service-content">
              <span className="managed-service-tag">03</span>
              <h2 className="managed-service-title">Campaign Tracking & Analytics</h2>
              <p className="managed-service-description">
                Every campaign we run is measured against real business outcomes — not vanity metrics. We build custom dashboards and reporting frameworks so you always know what's working, what's not, and where to invest next.
              </p>
              <ul className="managed-service-features">
                <li>
                  <strong>Custom Dashboards</strong>
                  <span>Real-time dashboards tailored to the KPIs that matter to your business — leads, conversions, revenue attribution.</span>
                </li>
                <li>
                  <strong>Multi-Channel Attribution</strong>
                  <span>Track the customer journey across channels to understand which touchpoints drive results.</span>
                </li>
                <li>
                  <strong>ROI Reporting</strong>
                  <span>Clear reporting that ties marketing spend directly to pipeline and revenue outcomes.</span>
                </li>
                <li>
                  <strong>Continuous Optimization</strong>
                  <span>Data-driven recommendations and adjustments to improve campaign performance month over month.</span>
                </li>
              </ul>
            </div>
            <div className="managed-service-visual">
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80"
                alt="Campaign analytics and tracking dashboards"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="managed-process">
        <div className="managed-container">
          <h2 className="managed-section-title">How It Works</h2>
          <p className="managed-process-subtitle">
            A straightforward engagement model designed around accountability and results
          </p>
          <div className="managed-process-grid">
            <div className="managed-process-step">
              <div className="managed-step-number">1</div>
              <h3 className="managed-step-title">Discovery</h3>
              <p className="managed-step-description">
                We learn your business, audience, competitive landscape, and current marketing efforts to identify gaps and opportunities.
              </p>
            </div>
            <div className="managed-process-step">
              <div className="managed-step-number">2</div>
              <h3 className="managed-step-title">Strategy</h3>
              <p className="managed-step-description">
                We build a tailored plan with clear goals, timelines, channel priorities, and measurable KPIs tied to business outcomes.
              </p>
            </div>
            <div className="managed-process-step">
              <div className="managed-step-number">3</div>
              <h3 className="managed-step-title">Execution</h3>
              <p className="managed-step-description">
                Our team handles the day-to-day — creating content, managing campaigns, optimizing workflows, and engaging your audience.
              </p>
            </div>
            <div className="managed-process-step">
              <div className="managed-step-number">4</div>
              <h3 className="managed-step-title">Reporting</h3>
              <p className="managed-step-description">
                Monthly performance reviews with transparent reporting, actionable insights, and strategic adjustments based on data.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="managed-cta">
        <div className="managed-container">
          <h2 className="managed-cta-title">Ready to Put Your Marketing on Autopilot?</h2>
          <p className="managed-cta-description">
            Let's talk about how our managed programs can drive consistent results without adding to your workload.
          </p>
          <Link to="/contact" className="btn-managed-primary">Speak to an Expert</Link>
        </div>
      </section>
    </div>
  );
};

export default ManagedPrograms;
